import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Filter, Search, Receipt, Edit2, Tag } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import useExpenseStore from '../../store/expenseStore';
import useCategoryStore from '../../store/categoryStore';
import useTagStore from '../../store/tagStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { PRIORITY_LABELS, PRIORITY_COLORS } from '../../utils/constants';

const ExpenseList = () => {
  const { userProfile } = useAuthStore();
  const { expenses, subscribeToExpenses, loading } = useExpenseStore();
  const { categories, getCategoryById } = useCategoryStore();
  const { tags, subscribeToTags, getTagsByIds } = useTagStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTagFilter, setSelectedTagFilter] = useState('');

  useEffect(() => {
    if (userProfile?.familyId) {
      subscribeToExpenses(userProfile.familyId);
      subscribeToTags(userProfile.familyId);
    }
  }, [userProfile?.familyId, subscribeToExpenses, subscribeToTags]);

  const getPriorityBadge = (priority) => {
    const color = PRIORITY_COLORS[priority];
    const label = PRIORITY_LABELS[priority];
    
    return (
      <span 
        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
        style={{ 
          backgroundColor: `${color}20`, 
          color: color 
        }}
      >
        {label}
      </span>
    );
  };

  const getCategoryDisplay = (categoryId) => {
    const category = getCategoryById(categoryId);
    if (!category) return { name: 'Unknown', icon: '❓', color: '#9E9E9E' };
    return category;
  };

  // Filter expenses based on search term and tag filter
  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = !searchTerm || 
      expense.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTag = !selectedTagFilter || 
      (expense.tags && expense.tags.includes(selectedTagFilter));
    
    return matchesSearch && matchesTag;
  });

  const getExpenseTags = (tagIds) => {
    if (!tagIds || tagIds.length === 0) return [];
    return getTagsByIds(tagIds);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Expenses</h1>
            <p className="text-gray-600">Loading your expenses...</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expenses</h1>
          <p className="text-gray-600">Track and manage your family's expenses</p>
        </div>
        <Link to="/expenses/add">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Expense
          </Button>
        </Link>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search expenses..."
                    className="pl-10"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <select
                  value={selectedTagFilter}
                  onChange={(e) => setSelectedTagFilter(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">All Tags</option>
                  {tags.map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      {tag.name}
                    </option>
                  ))}
                </select>
                {(searchTerm || selectedTagFilter) && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedTagFilter('');
                    }}
                    className="sm:w-auto w-full"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>
            {(searchTerm || selectedTagFilter) && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Filter className="h-4 w-4" />
                <span>
                  Showing {filteredExpenses.length} of {expenses.length} expenses
                  {searchTerm && ` matching "${searchTerm}"`}
                  {selectedTagFilter && ` with tag "${tags.find(t => t.id === selectedTagFilter)?.name}"`}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Expenses List */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Expenses</CardTitle>
          <CardDescription>
            {filteredExpenses.length} expense{filteredExpenses.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredExpenses.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                <Plus className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {expenses.length === 0 ? 'No expenses yet' : 'No matching expenses'}
              </h3>
              <p className="text-gray-600 mb-4">
                {expenses.length === 0 
                  ? 'Start by adding your first expense to track your family\'s spending.'
                  : 'Try adjusting your search or filter criteria.'
                }
              </p>
              {expenses.length === 0 && (
                <Link to="/expenses/add">
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Expense
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredExpenses.map((expense) => {
                const category = getCategoryDisplay(expense.category);
                const expenseDate = expense.date?.toDate ? expense.date.toDate() : new Date(expense.date);
                
                const expenseTags = getExpenseTags(expense.tags);
                
                return (
                  <div
                    key={expense.id}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {/* Mobile Layout */}
                    <div className="flex flex-col space-y-3 sm:hidden">
                      {/* Top row: Icon, Description, Amount */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                            style={{ backgroundColor: category.color }}
                          >
                            {category.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-medium text-gray-900 truncate">
                              {expense.description}
                            </h3>
                            <p className="text-lg font-semibold text-gray-900">
                              {formatCurrency(expense.amount)}
                            </p>
                          </div>
                        </div>
                        <Link to={`/expenses/edit/${expense.id}`}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                      
                      {/* Details row */}
                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                        <span>{category.name}</span>
                        {expense.subcategory && (
                          <>
                            <span>•</span>
                            <span>{expense.subcategory}</span>
                          </>
                        )}
                        <span>•</span>
                        <span>{formatDate(expenseDate)}</span>
                        <span>•</span>
                        {getPriorityBadge(expense.priority)}
                        {expense.receiptUrl && (
                          <>
                            <span>•</span>
                            <Receipt className="h-3 w-3 text-gray-400" />
                          </>
                        )}
                      </div>
                      
                      {/* Tags */}
                      {expenseTags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {expenseTags.map((tag) => (
                            <span
                              key={tag.id}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                              style={{ backgroundColor: tag.color }}
                            >
                              <Tag className="h-2 w-2 mr-1" />
                              {tag.name}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Notes */}
                      {expense.notes && (
                        <p className="text-xs text-gray-500">
                          {expense.notes}
                        </p>
                      )}
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden sm:flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        {/* Category Icon */}
                        <div className="flex-shrink-0">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                            style={{ backgroundColor: category.color }}
                          >
                            {category.icon}
                          </div>
                        </div>

                        {/* Expense Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="text-sm font-medium text-gray-900 truncate">
                              {expense.description}
                            </h3>
                            {expense.receiptUrl && (
                              <Receipt className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span>{category.name}</span>
                            {expense.subcategory && (
                              <>
                                <span>•</span>
                                <span>{expense.subcategory}</span>
                              </>
                            )}
                            <span>•</span>
                            <span>{formatDate(expenseDate)}</span>
                            <span>•</span>
                            {getPriorityBadge(expense.priority)}
                          </div>
                          {expense.notes && (
                            <p className="text-xs text-gray-500 mt-1 truncate">
                              {expense.notes}
                            </p>
                          )}
                          {expenseTags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {expenseTags.map((tag) => (
                                <span
                                  key={tag.id}
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                                  style={{ backgroundColor: tag.color }}
                                >
                                  <Tag className="h-2 w-2 mr-1" />
                                  {tag.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Amount and Actions */}
                        <div className="text-right flex items-center space-x-3">
                          <div>
                            <p className="text-lg font-semibold text-gray-900">
                              {formatCurrency(expense.amount)}
                            </p>
                            <p className="text-xs text-gray-500">
                              by {expense.paidBy === userProfile?.uid ? 'You' : 'Partner'}
                            </p>
                          </div>
                          <Link to={`/expenses/edit/${expense.id}`}>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Card */}
      {filteredExpenses.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0))}
                </p>
                <p className="text-sm text-gray-600">Total Spent</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {filteredExpenses.length}
                </p>
                <p className="text-sm text-gray-600">Transactions</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(filteredExpenses.length > 0 ? filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0) / filteredExpenses.length : 0)}
                </p>
                <p className="text-sm text-gray-600">Average</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExpenseList;
